"use client"

import { useState, useEffect } from "react"
import { Card, Form, Row, Col, Button, Spinner } from "react-bootstrap"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import { apiRequest, endpoints } from "../utils/api"
import { toast } from "react-hot-toast"

const HourlyWeatherChart = ({ location }) => {
  const [hourlyData, setHourlyData] = useState([])
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split("T")[0]
  })
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0]
  })

  // ✅ useEffect with async fetch inside
  useEffect(() => {
    if (location) {
      const fetchData = async () => {
        setLoading(true)
        try {
          const response = await apiRequest(
            `${endpoints.weather}/hourly?lat=${location.lat}&lng=${location.lng}&startDate=${startDate}&endDate=${endDate}`,
          )

          // Process data for chart display (sample every 6 hours)
          const processedData = response.hourlyData
            .filter((_, index) => index % 6 === 0)
            .map((item) => ({
              time:
                new Date(item.datetime).toLocaleDateString() +
                " " +
                new Date(item.datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              temperature: Math.round(item.temperature * 10) / 10,
              humidity: Math.round(item.humidity),
              soilTemp: Math.round(item.soilTemperature * 10) / 10,
              soilMoisture: Math.round(item.soilMoisture),
            }))

          setHourlyData(processedData)
        } catch (error) {
          console.error("Error fetching hourly data:", error)
          toast.error("Failed to fetch hourly weather data")
        } finally {
          setLoading(false)
        }
      }

      fetchData()
    }
  }, [location, startDate, endDate])

  const handleDateRangeUpdate = () => {
    // Changing startDate or endDate already triggers useEffect
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">📊 Hourly Weather Analysis</h5>
          </Col>
          <Col xs="auto">
            <Row className="g-2 align-items-center">
              <Col>
                <Form.Control type="date" size="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Col>
              <Col>
                <Form.Control type="date" size="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Col>
              <Col>
                <Button size="sm" variant="primary" onClick={handleDateRangeUpdate} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : "Update"}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading hourly weather data...</p>
          </div>
        ) : hourlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis yAxisId="temp" orientation="left" />
              <YAxis yAxisId="percent" orientation="right" />
              <Tooltip
                labelFormatter={(value) => `Time: ${value}`}
                formatter={(value, name) => [
                  `${value}${name.includes("temperature") || name.includes("Temp") ? "°C" : "%"}`,
                  name,
                ]}
              />
              <Legend />
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="temperature"
                stroke="#dc3545"
                strokeWidth={2}
                name="Air Temperature"
                dot={false}
              />
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="soilTemp"
                stroke="#fd7e14"
                strokeWidth={2}
                name="Soil Temperature"
                dot={false}
              />
              <Line
                yAxisId="percent"
                type="monotone"
                dataKey="humidity"
                stroke="#0dcaf0"
                strokeWidth={2}
                name="Humidity"
                dot={false}
              />
              <Line
                yAxisId="percent"
                type="monotone"
                dataKey="soilMoisture"
                stroke="#198754"
                strokeWidth={2}
                name="Soil Moisture"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-5 text-muted">
            <p>No hourly data available for the selected date range</p>
            <small>Try selecting a different date range or check your internet connection</small>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default HourlyWeatherChart
